import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ShieldAlert, Plus, Trash2, Save, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SEVERITIES = ["critical", "warning", "info"];
const SEV_TONE = {
  critical: "bg-red-50 text-red-700 border-red-500/30",
  warning:  "bg-amber-50 text-amber-700 border-amber-500/30",
  info:     "bg-sky-50 text-sky-700 border-sky-500/30",
};

export default function AlertSettings() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hasBreakTank, setHasBreakTank] = useState(false);

  const load = async () => {
    const r = await api.get("/alerts/rule-settings");
    let list = r.data;
    // Detect if any site has break tanks
    try {
      const ss = await api.get("/sites");
      const bt = (ss.data || []).some((s) => (s.break_tank_count || 0) > 0 || (s.total_height_m || 0) >= 90);
      setHasBreakTank(bt);
      if (bt && !list.some((x) => (x.alert_type || "").toLowerCase().includes("break tank"))) {
        list = [
          { rule_id: "bt_low",      alert_type: "Break Tank Low Level",         threshold: "< 25%",         severity: "critical", sms: true,  email: true,  whatsapp: false, is_active: true },
          { rule_id: "bt_pump_trip",alert_type: "Intermediate Pump Trip",       threshold: "Motor overload",severity: "critical", sms: true,  email: true,  whatsapp: false, is_active: true },
          { rule_id: "bt_overflow", alert_type: "Break Tank Overflow",          threshold: "Float trigger", severity: "warning",  sms: false, email: true,  whatsapp: false, is_active: true },
          { rule_id: "bt_pwr_fail", alert_type: "Intermediate Pump Power Failure", threshold: "MCC voltage lost", severity: "critical", sms: true, email: true, whatsapp: false, is_active: true },
          ...list,
        ];
      }
    } catch { /* ignore */ }
    setRows(list);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (i, patch) => {
    const c = [...rows]; c[i] = { ...c[i], ...patch }; setRows(c);
  };
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const add = () => setRows([...rows, {
    rule_id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
    alert_type: "", threshold: "", severity: "warning", sms: false, email: true, is_active: true,
  }]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/alerts/rule-settings", { rules: rows });
      toast.success("Settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="alert-settings-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => nav("/alerts")} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-2" data-testid="back-to-alerts"><ChevronLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Back to Alerts</button>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-8 h-8 text-orange-400" strokeWidth={2.5} /> Alert Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configure threshold rules, severity, and notification channels</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/15" onClick={add} data-testid="add-rule-btn"><Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Add Rule</Button>
          <Button className="f360-gradient border-0" onClick={save} disabled={busy} data-testid="save-rules-btn"><Save className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Save</Button>
        </div>
      </div>

      <div className="glass rounded-lg overflow-hidden" data-testid="rules-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">Alert Type</th>
                <th className="text-left py-3">Threshold</th>
                <th className="text-center py-3">Severity</th>
                <th className="text-center py-3">SMS</th>
                <th className="text-center py-3">Email</th>
                <th className="text-center py-3">Active</th>
                <th className="text-right px-5 py-3">—</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.rule_id} className="border-t border-gray-200" data-testid={`rule-row-${i}`}>
                  <td className="px-5 py-3">
                    <Input className="bg-gray-50 border-gray-200 h-8" value={r.alert_type}
                      onChange={(e) => update(i, { alert_type: e.target.value })}
                      placeholder="e.g. Hydrant Low Pressure" data-testid={`alert-type-${i}`} />
                  </td>
                  <td className="py-3">
                    <Input className="bg-gray-50 border-gray-200 h-8" value={r.threshold}
                      onChange={(e) => update(i, { threshold: e.target.value })}
                      placeholder="e.g. < 3.5 bar" data-testid={`threshold-${i}`} />
                  </td>
                  <td className="py-3 text-center">
                    <Select value={r.severity} onValueChange={(v) => update(i, { severity: v })}>
                      <SelectTrigger className={`h-8 w-28 mx-auto border ${SEV_TONE[r.severity] || "bg-gray-50 border-gray-200"}`} data-testid={`severity-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 text-center">
                    <Switch checked={r.sms} onCheckedChange={(v) => update(i, { sms: v })} data-testid={`sms-${i}`} />
                  </td>
                  <td className="py-3 text-center">
                    <Switch checked={r.email} onCheckedChange={(v) => update(i, { email: v })} data-testid={`email-${i}`} />
                  </td>
                  <td className="py-3 text-center">
                    <Switch checked={r.is_active} onCheckedChange={(v) => update(i, { is_active: v })} data-testid={`active-${i}`} />
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(i)} className="text-red-400 hover:text-red-200" data-testid={`del-rule-${i}`}><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-8">No rules configured. Click <b>Add Rule</b> to create one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
